/* @jsx h */
import { cn } from "../utils";
import { defineComponent, h, type VNode } from "vue";

export interface LemmonSwitcherOption<T = string> {
  value: T;
  label: string | VNode;
  icon?: string;
  mobileLabel?: string;
  ariaLabel?: string;
}

interface LemmonSwitcherProps<T = string> {
  options: LemmonSwitcherOption<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  buttonClassName?: string;
  activeButtonClassName?: string;
  inactiveButtonClassName?: string;
  labelClassName?: string;
  iconClassName?: string;
  indicatorClassName?: string;
  indicatorInsetPx?: number;
  iconOnlyOnMobile?: boolean;
}

export const LemmonSwitcher = defineComponent<LemmonSwitcherProps>({
  name: "LemmonSwitcher",
  props: [
    "options",
    "value",
    "onChange",
    "className",
    "buttonClassName",
    "activeButtonClassName",
    "inactiveButtonClassName",
    "labelClassName",
    "iconClassName",
    "indicatorClassName",
    "indicatorInsetPx",
    "iconOnlyOnMobile",
  ],
  setup(props) {
    return () => {
      const options = props.options || [];
      const indicatorInsetPx = Number(props.indicatorInsetPx ?? 2);
      const activeIndex = Math.max(
        0,
        options.findIndex((option) => option.value === props.value),
      );
      const hasActiveOption = options.some(
        (option) => option.value === props.value,
      );

      return (
        <div
          class={cn(
            "relative inline-grid min-w-0 flex-shrink-0 items-stretch overflow-hidden rounded-lg",
            props.className,
          )}
          style={{
            background: "var(--lemmon-switcher-background)",
            border: "1px solid var(--lemmon-switcher-border)",
            gridTemplateColumns: `repeat(${Math.max(options.length, 1)}, minmax(0, 1fr))`,
          }}
        >
          {hasActiveOption ? (
            <div
              aria-hidden="true"
              class={cn(
                "pointer-events-none absolute transition-transform duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)] rounded-md",
                props.indicatorClassName,
              )}
              style={{
                left: `${indicatorInsetPx}px`,
                width: `calc(${100 / Math.max(options.length, 1)}% - ${indicatorInsetPx}px)`,
                transform: `translateX(calc(${activeIndex * 100}% + ${activeIndex * indicatorInsetPx}px))`,
                background: "var(--lemmon-switcher-indicator-background)",
                "box-shadow": "var(--lemmon-switcher-indicator-shadow)",
              }}
            >
              <div
                class="h-full rounded-md"
                style={{
                  background:
                    "var(--lemmon-switcher-indicator-overlay-background)",
                  boxShadow: "var(--lemmon-switcher-indicator-shadow)",
                }}
              />
            </div>
          ) : null}

          {options.map((option) => {
            const isActive = option.value === props.value;

            return (
              <button
                key={String(option.value)}
                type="button"
                aria-pressed={isActive}
                aria-label={option.ariaLabel}
                class={cn(
                  "relative flex min-w-0 items-center justify-center transition-all duration-300 cursor-pointer",
                  isActive
                    ? "text-accent/90"
                    : "text-base-content/90 hover:text-base-content/50",
                  props.buttonClassName,
                  isActive
                    ? props.activeButtonClassName
                    : props.inactiveButtonClassName,
                )}
                onClick={() => props.onChange(option.value)}
              >
                {option.icon ? (
                  <span
                    class={cn(
                      "material-symbols-outlined shrink-0 leading-none",
                      props.iconClassName,
                    )}
                  >
                    {option.icon}
                  </span>
                ) : null}

                <span
                  class={cn(
                    "truncate",
                    props.labelClassName,
                    props.iconOnlyOnMobile && "hidden sm:inline",
                  )}
                >
                  {option.label}
                </span>

                {props.iconOnlyOnMobile && option.mobileLabel ? (
                  <span class="sr-only">{option.mobileLabel}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      );
    };
  },
}) as new () => {
  $props: LemmonSwitcherProps;
};
